import React from 'react';
import { Minus, Plus, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../ui';

export type CreditSourceType = 'absence' | 'cancellation' | 'used' | 'expired';

export interface CreditHistoryEarnedFromSession {
  dateYmd: string;
  startTime?: string;
  endTime?: string;
}

export type CreditHistoryRedeemedSession =
  | {
      kind: 'session';
      dateYmd: string;
      lessonName?: string;
      startTime?: string;
      endTime?: string;
    }
  | {
      kind: 'full_term';
      lessonName?: string;
      label: string;
    };

export interface CreditHistoryItem {
  id: string;
  date: string;
  type: CreditSourceType;
  lessonName: string;
  skillLevel?: string;
  sportLabel?: string;
  academySportId?: string;
  status: 'available' | 'used' | 'expired';
  expiresAt?: string;
  usedAt?: string;
  earnedFromSession?: CreditHistoryEarnedFromSession;
  redeemedSession?: CreditHistoryRedeemedSession;
}

function formatYmdToLocalDate(ymd: string): string {
  const parts = ymd.split('T')[0].split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatHhMm(hhmm: string | undefined): string {
  if (!hhmm) return '';
  const seg = hhmm.split(':');
  const h = parseInt(seg[0] ?? '0', 10);
  const min = parseInt(seg[1] ?? '0', 10);
  if (Number.isNaN(h)) return hhmm;
  const dt = new Date(2000, 0, 1, h, Number.isNaN(min) ? 0 : min);
  return dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatTimeRange(start?: string, end?: string): string {
  const a = formatHhMm(start);
  const b = formatHhMm(end);
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
}

interface CreditsHistoryProps {
  credits: CreditHistoryItem[];
  isLoading?: boolean;
}

export const CreditsHistory: React.FC<CreditsHistoryProps> = ({
  credits,
  isLoading = false,
}) => {
  const getTypeInfo = (type: CreditSourceType) => {
    switch (type) {
      case 'absence':
        return {
          icon: Plus,
          label: 'Earned - Absence',
          color: 'text-green-600',
          bg: 'bg-green-50',
        };
      case 'cancellation':
        return {
          icon: Plus,
          label: 'Earned - Cancellation',
          color: 'text-green-600',
          bg: 'bg-green-50',
        };
      case 'used':
        return {
          icon: Minus,
          label: 'Used',
          color: 'text-blue-600',
          bg: 'bg-blue-50',
        };
      case 'expired':
        return {
          icon: AlertCircle,
          label: 'Expired',
          color: 'text-red-600',
          bg: 'bg-red-50',
        };
      default:
        return {
          icon: Plus,
          label: 'Unknown',
          color: 'text-gray-600',
          bg: 'bg-gray-50',
        };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Available</span>;
      case 'used':
        return <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Used</span>;
      case 'expired':
        return <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Expired</span>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-center text-gray-600">Loading credits history...</p>
        </CardContent>
      </Card>
    );
  }

  if (credits.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-center text-gray-600">No credits yet. Report an absence to earn your first credit!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="font-semibold text-gray-900">Credits History</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {credits.map((item) => {
            const typeInfo = getTypeInfo(item.type);
            const Icon = typeInfo.icon;

            return (
              <div key={item.id} className={`${typeInfo.bg} border border-gray-200 rounded-lg p-4`}>
                <div className="flex items-start gap-4">
                  <Icon className={`w-5 h-5 ${typeInfo.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="font-medium text-gray-900">{typeInfo.label}</p>
                      {(item.type === 'absence' || item.type === 'cancellation') && getStatusBadge(item.status)}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{item.lessonName}</p>
                    {item.sportLabel ? (
                      <p className="text-xs text-muted-foreground mb-2">Sport: {item.sportLabel}</p>
                    ) : null}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{new Date(item.date).toLocaleDateString()}</span>
                      {item.skillLevel && <span>{item.skillLevel}</span>}
                    </div>
                    {item.earnedFromSession && (
                      <p className="text-xs text-gray-600 mt-2">
                        <span className="font-medium text-gray-700">Missed / cancelled session: </span>
                        {formatYmdToLocalDate(item.earnedFromSession.dateYmd)}
                        {formatTimeRange(item.earnedFromSession.startTime, item.earnedFromSession.endTime)
                          ? ` · ${formatTimeRange(item.earnedFromSession.startTime, item.earnedFromSession.endTime)}`
                          : ''}
                      </p>
                    )}
                    {item.status === 'used' && item.redeemedSession && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium text-gray-700">Credit used for: </span>
                        {item.redeemedSession.kind === 'session' ? (
                          <>
                            {item.redeemedSession.lessonName ? (
                              <>
                                <span className="text-gray-800">{item.redeemedSession.lessonName}</span>
                                {' — '}
                              </>
                            ) : null}
                            {formatYmdToLocalDate(item.redeemedSession.dateYmd)}
                            {formatTimeRange(item.redeemedSession.startTime, item.redeemedSession.endTime)
                              ? ` · ${formatTimeRange(item.redeemedSession.startTime, item.redeemedSession.endTime)}`
                              : ''}
                          </>
                        ) : (
                          <>
                            {item.redeemedSession.lessonName ? (
                              <>
                                <span className="text-gray-800">{item.redeemedSession.lessonName}</span>
                                {' — '}
                              </>
                            ) : null}
                            {item.redeemedSession.label}
                          </>
                        )}
                      </p>
                    )}
                    {item.expiresAt && item.status === 'available' && (
                      <p className="text-xs text-amber-600 mt-1">
                        Expires: {new Date(item.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                    {item.usedAt && item.status === 'used' && !item.redeemedSession && (
                      <p className="text-xs text-blue-600 mt-1">
                        Redeemed: {new Date(item.usedAt).toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CreditsHistory;
